import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const sizeLines = (sel) =>
    Object.entries(sel?.sizes || {}).filter(([, q]) => Number(q) > 0)
        .map(([k, q]) => `${k.replace("|", " ")} × ${q}`);

const PRINT_CSS = `
@media print {
  html, body { background: #fff !important; }
  .no-print { display: none !important; }
  .ticket-shell { background: #fff !important; padding: 0 !important; }
  .ticket-paper { box-shadow: none !important; margin: 0 !important; padding: 0.35in !important; }
  @page { size: letter; margin: 0.4in; }
}`;

function Line({ item, i }) {
    const sizes = sizeLines(item.selections);
    return (
        <div className="break-inside-avoid border-t-2 border-black py-3" data-testid={`ticket-line-${i}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-500">Line {i + 1} · {item.category || "custom"}</p>
                    <p className="font-display text-xl font-black leading-tight">{item.product_name}</p>
                    {item.summary && <p className="text-sm text-neutral-600">{item.summary}</p>}
                </div>
                <div className="shrink-0 text-right">
                    <p className="text-3xl font-black leading-none">{item.quantity}</p>
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">qty</p>
                    <p className="mt-1 font-mono text-sm">{money(item.line_total)}</p>
                </div>
            </div>

            <div className="mt-2 grid gap-3 sm:grid-cols-2">
                {sizes.length > 0 && (
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Size run</p>
                        <ul className="mt-1 text-sm">{sizes.map((s) => <li key={s}>▢ {s}</li>)}</ul>
                    </div>
                )}
                {(item.selections?.locations || []).length > 0 && (
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Print locations</p>
                        <p className="mt-1 text-sm capitalize">{item.selections.locations.join(" · ")}</p>
                    </div>
                )}
            </div>

            {(item.proof?.url || (item.artwork || []).length > 0) && (
                <div className="mt-3 flex flex-wrap items-start gap-4">
                    {item.proof?.url && (
                        <img src={item.proof.url} alt="proof" crossOrigin="anonymous"
                            className="h-36 w-36 rounded border border-neutral-300 bg-neutral-100 object-contain"
                            data-testid={`ticket-proof-${i}`} />
                    )}
                    {(item.artwork || []).length > 0 && (
                        <div>
                            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Files</p>
                            <ul className="mt-1 space-y-0.5 text-xs">
                                {item.artwork.map((a, x) => <li key={x} className="font-mono">{a.filename || `artwork ${x + 1}`}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function VendorTicket() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/vendor/orders/${id}`)
            .then(({ data }) => setOrder(data.order))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load that order"));
    }, [id]);

    if (err) return (
        <div className="flex min-h-screen items-center justify-center bg-brand-ink text-center text-zinc-300" data-testid="ticket-error">
            <div><p>{err}</p>
                <button onClick={() => navigate("/account/orders")} className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-green">← order inbox</button>
            </div>
        </div>
    );
    if (!order) return <div className="flex min-h-screen items-center justify-center bg-brand-ink"><Loader2 className="animate-spin text-brand-green" /></div>;

    const total = order.items.reduce((s, i) => s + (Number(i.line_total) || 0), 0);
    const pieces = order.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);

    return (
        <div className="ticket-shell min-h-screen bg-neutral-200 py-6 text-black" data-testid="vendor-ticket">
            <style>{PRINT_CSS}</style>

            <div className="no-print mx-auto mb-4 flex max-w-[8.5in] items-center justify-between gap-3 px-4">
                <button onClick={() => navigate("/account/orders")} data-testid="ticket-back"
                    className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#ffffff]">
                    <ArrowLeft size={13} /> order inbox
                </button>
                <button onClick={() => window.print()} data-testid="ticket-print"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-black">
                    <Printer size={14} /> print ticket
                </button>
            </div>

            <div className="ticket-paper mx-auto max-w-[8.5in] bg-white p-8 shadow-xl">
                <div className="flex items-start justify-between gap-6 border-b-4 border-black pb-4">
                    <div>
                        <p className="text-[11px] font-black uppercase tracking-[0.3em]">13thav</p>
                        <p className="font-display text-2xl font-black leading-tight">{order.items[0]?.store_name || "Work ticket"}</p>
                        <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">Production work ticket</p>
                    </div>
                    <div className="text-right">
                        <p className="font-mono text-2xl font-black tracking-tight" data-testid="ticket-order-number">{order.order_number}</p>
                        <p className="mt-1 inline-block border-2 border-black px-2 py-0.5 text-[11px] font-black uppercase tracking-[0.16em]">
                            {order.status?.replace(/_/g, " ")}
                        </p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em]" data-testid="ticket-paid">
                            {order.paid ? `Paid${order.paid_by === "card" ? " · card" : ""}` : "Unpaid — collect on pickup"}
                        </p>
                    </div>
                </div>

                <div className="grid gap-4 border-b-2 border-black py-4 sm:grid-cols-3">
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Customer</p>
                        <p className="font-bold" data-testid="ticket-customer">{order.contact?.name || "Walk-in"}</p>
                        <p className="text-sm">{order.contact?.phone || "—"}</p>
                        <p className="break-all text-sm">{order.contact?.email || ""}</p>
                    </div>
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Ordered</p>
                        <p className="text-sm">{new Date(order.created_at).toLocaleString()}</p>
                        <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Proof</p>
                        <p className="text-sm capitalize">{order.proof_status || "none"}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Pieces / your total</p>
                        <p className="font-display text-3xl font-black leading-none">{pieces}</p>
                        <p className="font-mono text-lg font-bold" data-testid="ticket-total">{money(total)}</p>
                    </div>
                </div>

                {order.notes && (
                    <div className="border-b-2 border-black py-3" data-testid="ticket-notes">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Customer notes</p>
                        <p className="text-sm">{order.notes}</p>
                    </div>
                )}

                <div data-testid="ticket-lines">
                    {order.items.map((it, i) => <Line key={it.uid || i} item={it} i={i} />)}
                </div>

                {(order.comments || []).length > 0 && (
                    <div className="border-t-2 border-black pt-3" data-testid="ticket-comments">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">Shop notes</p>
                        {order.comments.map((c) => (
                            <p key={c.id} className="text-sm">• {c.body} <span className="text-neutral-500">— {c.author}</span></p>
                        ))}
                    </div>
                )}

                <div className="mt-6 grid grid-cols-3 gap-4 border-t-4 border-black pt-6 text-[11px] font-bold uppercase tracking-[0.16em]">
                    {["Printed by", "QC check", "Packed / date"].map((l) => (
                        <div key={l}><div className="h-10 border-b border-black" /><p className="mt-1 text-neutral-500">{l}</p></div>
                    ))}
                </div>
            </div>
        </div>
    );
}
